import { useMemo, useState, type ReactNode } from "react";
import {
  Clock,
  CurrencyDollar,
  PaperPlaneTilt,
  SealCheck,
  UserCircle,
} from "@phosphor-icons/react";
import { Button, Spinner } from "@/components/ui";
import { PressEyebrow, PressPanel, StatusPill } from "@/components/pullpress/PressVisuals";
import type { EditorJobStatus, EditorMessage, EditorServiceJob } from "@/types";

interface EditorServicePanelProps {
  job: EditorServiceJob | null;
  messages: readonly EditorMessage[];
  loading: boolean;
  messagesLoading: boolean;
  error: string | null;
  messagesError: string | null;
  isAuthor: boolean;
  isAssignedEditor: boolean;
  requesting: boolean;
  posting: boolean;
  submitting: boolean;
  approving: boolean;
  canRequest: boolean;
  canSubmitEditorRevision: boolean;
  canApproveEditorRevision: boolean;
  onRequest: () => void;
  onPostMessage: (body: string) => void;
  onSubmitEditorRevision: () => void;
  onApproveEditorRevision: () => void;
}

export function EditorServicePanel({
  job,
  messages,
  loading,
  messagesLoading,
  error,
  messagesError,
  isAuthor,
  isAssignedEditor,
  requesting,
  posting,
  submitting,
  approving,
  canRequest,
  canSubmitEditorRevision,
  canApproveEditorRevision,
  onRequest,
  onPostMessage,
  onSubmitEditorRevision,
  onApproveEditorRevision,
}: EditorServicePanelProps) {
  const [draftMessage, setDraftMessage] = useState("");
  const deadline = useMemo(() => deadlineLabel(job), [job]);
  const remaining = useMemo(() => remainingLabel(job), [job]);

  return (
    <PressPanel className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <PressEyebrow>Real editor</PressEyebrow>
          <h2 className="mt-2 font-display text-xl font-semibold text-ink-1">
            {job ? editorJobTitle(job.status) : "Bring in an editor"}
          </h2>
        </div>
        {job && <StatusPill tone={statusTone(job.status)}>{statusLabel(job.status)}</StatusPill>}
      </div>

      {loading ? (
        <div className="mt-4"><Spinner /></div>
      ) : error ? (
        <p className="mt-4 font-ui text-sm leading-as-read text-flag">{error}</p>
      ) : job ? (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <EditorMetric icon={<Clock size={15} />} label="Deadline" value={deadline} />
            <EditorMetric icon={<CurrencyDollar size={15} />} label="Editor pay" value={formatMoney(job.editor_payout_cents, job.currency)} />
          </div>

          {job.assigned_editor_name && (
            <div className="flex items-center gap-2 rounded-as-2 border border-rule bg-paper px-3 py-2">
              <UserCircle size={18} className="text-walnut" />
              <div className="min-w-0">
                <div className="truncate font-ui text-sm font-semibold text-ink-1">{job.assigned_editor_name}</div>
                <div className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">{remaining}</div>
              </div>
            </div>
          )}

          {canSubmitEditorRevision && (
            <Button variant="walnut" className="w-full" onClick={onSubmitEditorRevision} disabled={submitting}>
              <PaperPlaneTilt size={16} className="mr-2" />
              {submitting ? "Sending..." : "Send revision to author"}
            </Button>
          )}

          {canApproveEditorRevision && (
            <Button variant="walnut" className="w-full" onClick={onApproveEditorRevision} disabled={approving}>
              <SealCheck size={16} className="mr-2" />
              {approving ? "Approving..." : "Approve editor revision"}
            </Button>
          )}

          <div className="border-t border-rule pt-4">
            <PressEyebrow>Messages</PressEyebrow>
            <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1">
              {messagesLoading ? (
                <Spinner />
              ) : messagesError ? (
                <p className="font-ui text-sm leading-as-read text-flag">{messagesError}</p>
              ) : messages.length > 0 ? (
                messages.map((message) => (
                  <MessageBubble key={message.id} message={message} authoredByViewer={messageAuthoredByViewer(message, isAuthor, isAssignedEditor)} />
                ))
              ) : (
                <p className="font-ui text-sm leading-as-read text-ink-3">No editor notes yet.</p>
              )}
            </div>
            <form
              className="mt-3 flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                const body = draftMessage.trim();
                if (body.length === 0) return;
                onPostMessage(body);
                setDraftMessage("");
              }}
            >
              <input
                value={draftMessage}
                onChange={(event) => setDraftMessage(event.target.value)}
                maxLength={2000}
                className="min-h-11 min-w-0 flex-1 rounded-as-2 border border-rule bg-paper px-3 font-ui text-sm text-ink-1 outline-none focus:border-walnut"
                placeholder="Ask or answer here"
              />
              <Button variant="secondary" size="sm" disabled={posting || draftMessage.trim().length === 0}>
                <PaperPlaneTilt size={16} />
              </Button>
            </form>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <p className="font-ui text-sm leading-as-read text-ink-2">
            Add a human line editor with a 30-day window to help the manuscript become ready for approval.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <EditorMetric icon={<Clock size={15} />} label="Window" value="30 days" />
            <EditorMetric icon={<CurrencyDollar size={15} />} label="Editor pay" value="$500" />
          </div>
          {canRequest ? (
            <Button variant="walnut" className="w-full" onClick={onRequest} disabled={requesting}>
              {requesting ? "Requesting..." : "Request a real editor"}
            </Button>
          ) : (
            <p className="font-ui text-sm leading-as-read text-ink-3">
              Editor service opens when the manuscript enters the proof window.
            </p>
          )}
        </div>
      )}
    </PressPanel>
  );
}

function EditorMetric({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-as-2 border border-rule bg-paper px-3 py-2">
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-ui text-sm font-semibold text-ink-1">{value}</div>
    </div>
  );
}

function MessageBubble({ message, authoredByViewer }: { message: EditorMessage; authoredByViewer: boolean }) {
  return (
    <div className={`rounded-as-2 border px-3 py-2 ${authoredByViewer ? "border-walnut-soft bg-walnut-tint" : "border-rule bg-paper"}`}>
      <div className="font-mono text-[10px] uppercase tracking-as-eyebrow text-ink-3">
        {message.sender_role} · {formatTimestamp(message.created_at)}
      </div>
      <p className="mt-1 whitespace-pre-wrap font-ui text-sm leading-as-read text-ink-1">{message.body}</p>
    </div>
  );
}

function messageAuthoredByViewer(message: EditorMessage, isAuthor: boolean, isAssignedEditor: boolean): boolean {
  return (isAuthor && message.sender_role === "author") || (isAssignedEditor && message.sender_role === "editor");
}

function editorJobTitle(status: EditorJobStatus): string {
  if (status === "pending_match") return "Finding the editor";
  if (status === "author_review") return "Revision ready for approval";
  if (status === "completed") return "Editor work complete";
  if (status === "cancelled") return "Service cancelled";
  return "Editor at work";
}

function statusLabel(status: EditorJobStatus): string {
  return status.replace(/_/gu, " ");
}

function statusTone(status: EditorJobStatus): "draft" | "progress" | "editing" | "ready" | "published" | "blocked" {
  if (status === "pending_match") return "progress";
  if (status === "assigned" || status === "in_edit" || status === "revision_requested") return "editing";
  if (status === "author_review") return "ready";
  if (status === "approved" || status === "completed") return "published";
  if (status === "late" || status === "cancelled") return "blocked";
  return "draft";
}

function deadlineLabel(job: EditorServiceJob | null): string {
  if (!job?.due_at) return "Unassigned";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(job.due_at.toMillis()));
}

function remainingLabel(job: EditorServiceJob | null): string {
  if (!job?.due_at) return "No deadline set";
  const msRemaining = job.due_at.toMillis() - Date.now();
  const days = Math.ceil(msRemaining / (24 * 60 * 60 * 1000));
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} late`;
  if (days === 0) return "Due today";
  return `${days} day${days === 1 ? "" : "s"} remaining`;
}

function formatMoney(cents: number, currency: "USD"): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
}

function formatTimestamp(timestamp: { toMillis: () => number } | null | undefined): string {
  if (!timestamp) return "now";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(
    new Date(timestamp.toMillis()),
  );
}
